'use client';

import { useTranslations } from 'next-intl';
import { HStack } from '@terra/ui-styled-system';
import { Add01Icon, RefreshIcon, UnavailableIcon } from '@terra/ui-icons';
import { Badge } from '../../../components/badge';
import { IconButton } from '../../../components/icon-button';
import { Skeleton } from '../../../components/skeleton';
import { Spinner } from '../../../components/spinner';
import { Tooltip } from '../../../components/tooltip';
import { SettingsEmptyState } from '../../settings/SettingsEmptyState';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsRow } from '../../settings/SettingsRow';
import { SettingsSection } from '../../settings/SettingsSection';
import { InternalNetworkAdvertisedRoutesField } from './InternalNetworkAdvertisedRoutesField';
import { InternalNetworkTunnelArtifactsDialog } from './InternalNetworkTunnelArtifactsDialog';
import {
  resolveTunnelEnrollmentStatusColor,
  type InternalNetworkTunnelEnrollmentRow,
} from './internal-connection.model';
import {
  useInternalNetworkTunnelEnrollmentActions,
  type InternalNetworkTunnelEnrollmentCallbacks,
} from './useInternalNetworkTunnelEnrollmentActions';

export type InternalNetworkTunnelEnrollmentsPanelProps =
  InternalNetworkTunnelEnrollmentCallbacks & {
    tunnelEnrollments: InternalNetworkTunnelEnrollmentRow[];
    isLoading?: boolean;
    isReadOnly?: boolean;
  };

type TunnelEnrollmentRowActionsProps = {
  isReprovisioning: boolean;
  isRevoking: boolean;
  onReprovision: () => void;
  onRevoke: () => void;
};

function TunnelEnrollmentRowActions({
  isReprovisioning,
  isRevoking,
  onReprovision,
  onRevoke,
}: TunnelEnrollmentRowActionsProps) {
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.tunnel',
  );
  const isBusy = isReprovisioning || isRevoking;

  return (
    <>
      <Tooltip title={t('reprovision')}>
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={t('reprovision')}
          onClick={onReprovision}
          disabled={isBusy}
        >
          {isReprovisioning ? <Spinner size="sm" /> : <RefreshIcon size={16} />}
        </IconButton>
      </Tooltip>
      <Tooltip title={t('revoke')}>
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={t('revoke')}
          onClick={onRevoke}
          disabled={isBusy}
        >
          {isRevoking ? <Spinner size="sm" /> : <UnavailableIcon size={16} />}
        </IconButton>
      </Tooltip>
    </>
  );
}

export function InternalNetworkTunnelEnrollmentsPanel({
  tunnelEnrollments,
  isLoading,
  isReadOnly,
  ...callbacks
}: InternalNetworkTunnelEnrollmentsPanelProps) {
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.tunnel',
  );
  const actions = useInternalNetworkTunnelEnrollmentActions(callbacks);

  return (
    <>
      <SettingsSection title={t('title')}>
        {tunnelEnrollments.length === 0 && !isReadOnly ? (
          <InternalNetworkAdvertisedRoutesField
            values={actions.advertisedRoutes}
            onChange={actions.setAdvertisedRoutes}
            disabled={actions.isCreating}
          />
        ) : null}
        <SettingsCard>
          <SettingsRow
            label={t('title')}
            value={t('description')}
            control={
              isReadOnly ? null : (
                <Tooltip title={t('create')}>
                  <IconButton
                    size="xs"
                    variant="outline"
                    aria-label={t('create')}
                    onClick={() => void actions.createTunnelEnrollment()}
                    disabled={actions.isCreating}
                  >
                    {actions.isCreating ? (
                      <Spinner size="sm" />
                    ) : (
                      <Add01Icon size={16} />
                    )}
                  </IconButton>
                </Tooltip>
              )
            }
          />
          {isLoading ? (
            <Skeleton h="80px" w="full" />
          ) : tunnelEnrollments.length === 0 ? (
            <SettingsEmptyState>{t('empty')}</SettingsEmptyState>
          ) : (
            tunnelEnrollments.map((enrollment) => (
              <SettingsRow
                key={enrollment.id}
                label={enrollment.id}
                value={enrollment.advertisedRoutes.join(', ')}
                control={
                  <HStack gap={2}>
                    <Badge
                      variant="outline"
                      size="sm"
                      colorPalette={resolveTunnelEnrollmentStatusColor(
                        enrollment.status,
                      )}
                    >
                      {t(`status.${enrollment.status}`)}
                    </Badge>
                    {enrollment.status !== 'REVOKED' && !isReadOnly ? (
                      <TunnelEnrollmentRowActions
                        isReprovisioning={
                          actions.reprovisioningEnrollmentId === enrollment.id
                        }
                        isRevoking={
                          actions.revokingEnrollmentId === enrollment.id
                        }
                        onReprovision={() =>
                          void actions.reprovisionTunnelEnrollment(
                            enrollment.id,
                          )
                        }
                        onRevoke={() =>
                          void actions.revokeTunnelEnrollment(enrollment.id)
                        }
                      />
                    ) : null}
                  </HStack>
                }
              />
            ))
          )}
        </SettingsCard>
      </SettingsSection>

      <InternalNetworkTunnelArtifactsDialog
        dialog={actions.artifactsDialog}
        onClose={actions.closeArtifactsDialog}
      />
    </>
  );
}
